import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  CheckCircle,
  AlertCircle,
  XCircle,
  ArrowLeft,
} from "lucide-react";
import host from "../../global.jsx";
import { getWhiteLabelFromJobLink } from "../../utils/whitelabel";
import PhoneInput, {
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
// import "react-phone-number-input/style.css";
import "./phoneNumberStyle.css";
import CustomATSForm from "./CustomATSForm";
import StandardATSForm from "./StandardATSForm";
import CustomDropdown from "../../components/common/CustomDropdown";
import JobDescriptionViewer, { JobDetails, Header } from "./JobDescriptionViewer";
import JobFairIntro from "./JobFairIntro";



const PublicJobApplication = () => {
  const { linkToken } = useParams();
  const [jobDetails, setJobDetails] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "description"; // "description" | "apply"

  const setView = (newView) => {
      setSearchParams((prev) => {
          prev.set("view", newView);
          return prev;
      });
  };
  const [organizationName, setOrganizationName] = useState("Loading...");
  const [isLoading, setIsLoading] = useState(true);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [applicationClosed, setApplicationClosed] = useState(false);
  const [branding, setBranding] = useState(null);

  // White labeling State
  const [isWhiteLabelingEnable, setisWhiteLabelingEnable] = useState(false);

  const [enableCustomATSQuestions, setEnableCustomATSQuestions] = useState(false);
  const [jobFairIntroDismissed, setJobFairIntroDismissed] = useState(false);


  useEffect(() => {
    fetchJobDetails();
    fetchBranding();
  }, [linkToken]);

  const fetchBranding = async () => {
    try {
      const brandingConfig = await getWhiteLabelFromJobLink(linkToken);
      // Remove Console Log
      // console.log({brandingConfig})
      setBranding(brandingConfig);
      setisWhiteLabelingEnable(brandingConfig.whitelabelEnabled);
    } catch (error) {
      console.error("Failed to load white-label branding:", error);
      setBranding(null);
    }
  };

  const fetchOrganizationName = async (adminId) => {
    try {
      const response = await axios.get(
        `${host}/get-organization-by-admin/${adminId}`
      );
      setOrganizationName(response.data.OrganizationName || "Company");
    } catch (error) {
      setOrganizationName("Company");
    }
  };

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(
        `${host}/public/job-details/${linkToken}`
      );
      setJobDetails(response.data);

      setEnableCustomATSQuestions(
        response.data.custom_ats_form_questions === true
      );

      // Set organization name from response
      if (response.data.organization_name) {
        setOrganizationName(response.data.organization_name);
      } else {
        const adminId =
          response.data.admin_id ||
          response.data.created_by ||
          response.data.org_admin_id;
        if (adminId) {
          await fetchOrganizationName(adminId);
        } else {
          setOrganizationName("Company");
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching job details:", error);
      if (error.response?.status === 404) {
        const errorMessage = error.response?.data?.detail || "";
        if (errorMessage.includes("no longer accepting applications")) {
          setApplicationClosed(true);
        } else {
          toast.error("This job link is invalid or has expired.");
        }
      } else {
        toast.error("Unable to load job details. Please try again.");
      }
      setIsLoading(false);
    }
  };

  //  Temp Patch Need To Fix In Future
  // if(!fetched){
  //   return <></>
  // }


  if (isLoading) {
    return (
      <div className={`min-h-screen  flex items-center justify-center ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (applicationClosed) {
    return (
      <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'} flex items-center justify-center p-4`}>
        <div className="max-w-md w-full">
          <div className="glass-effect bg-white rounded-3xl p-8 text-center">
            <div className="mb-6">
              <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            </div>
            <h2 className="text-2xl font-medium text-gray-700 mb-4">
              Position Closed
            </h2>
            <p className="text-gray-600 mb-6">
              This position is no longer accepting new applications.
            </p>
            <p className="text-gray-600 text-sm">
              Thank you for your interest! Please explore other opportunities on
              the company's career page.
            </p>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (!jobDetails) {
    return (
      <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'} flex items-center justify-center`}>
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className={`text-2xl font-medium mb-2 ${isWhiteLabelingEnable ? 'text-gray-700 ' : 'text-gray-300'}`}>Invalid Link</h2>
          <p className="text-gray-500">
            This job application link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  if (applicationSubmitted) {
    return (
      <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'} flex items-center justify-center p-4`}>
        <div className="max-w-2xl w-full ">
          <div className={`rounded-3xl p-6 text-center ${isWhiteLabelingEnable ? 'bg-white shadow-sm backdrop-blur-sm ' : 'glass-effect'}`}>
            <div className="flex justify-end items-center">
              <XCircle
                className={`${isWhiteLabelingEnable ? 'text-gray-700' : 'text-white'}`}
                onClick={() => window.history.back()}
              />
            </div>
            <div className="mb-6">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            </div>
            <h2 className={`text-3xl font-medium mb-4 ${isWhiteLabelingEnable ? 'text-gray-700 ' : 'text-white'}`}>
              Thank You!
            </h2>
            <p className={` mb-1 ${isWhiteLabelingEnable ? 'text-gray-600' : "text-gray-300"}`}>
              Your application for{" "}
              <span className={`font-medium  ${isWhiteLabelingEnable ? 'text-[#1569c4] ' : 'text-[#C1A0D0]'}`}>
                {jobDetails.job_role}
              </span>{" "}
              has been submitted to  <span className={`font-medium  ${isWhiteLabelingEnable ? 'text-[#1569c4] ' : 'text-[#C1A0D0]'}`}>
                {branding?.companyName}
              </span>{" "}
            </p>
            <p className={` mb-4 ${isWhiteLabelingEnable ? 'text-gray-600' : "text-gray-300"}`}>
              Our recruiter will reach out to you soon.
            </p>
            <div className="flex justify-center items-center mt-3 space-x-2">
              <span className={` text-sm ${isWhiteLabelingEnable ? 'text-gray-600' : "text-gray-300"}`}>Powered by GenieHire</span>
              {/* Need To Change Logo Here  */}
              {/* <img
                src={isWhiteLabelingEnable ? '/genie-demo-logo-black.png' : '/genie-demo-logo.png'}
                alt="GenieHire"
                className="h-4 object-contain"
              /> */}
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  // ── VIEW: Job fair intro (only orgs with job_fair_intro_enabled) ─────────
  if (view === "description" && jobDetails.job_fair_intro_enabled && !jobFairIntroDismissed) {
    return (
      <JobFairIntro
        organizationName={organizationName}
        branding={branding}
        isWhiteLabelingEnable={isWhiteLabelingEnable}
        onNext={() => setJobFairIntroDismissed(true)}
      />
    );
  }

  // ── VIEW: Description (JobDescriptionViewer layout) ──────────────────────
  if (view === "description") {
    return (
      <div className="min-h-screen bg-[#F5F4FA]">
        <JobDescriptionViewer
          jobDetails={jobDetails}
          organizationName={organizationName}
          branding={branding}
          isWhiteLabelingEnable={isWhiteLabelingEnable}
          isLoading={isLoading}
          onApply={() => setView("apply")}
        />
        <ToastContainer />
      </div>
    );
  }

  // ── VIEW: Apply (ATS form left, Job Details right) ────────────────────────
  return (
    <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#F1F4FB]' : 'app-background'}`}>
      
      {/* Reused Header */}
      <Header 
        jobDetails={jobDetails}
        organizationName={organizationName}
        branding={branding}
        isWhiteLabelingEnable={isWhiteLabelingEnable}
      />

      {/* Body - Layout matches JobDescriptionViewer */}
      <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* LEFT – ATS Form */}
          <div className="flex-1 flex flex-col gap-2">
            
            {/* Back Button */}
            <div className="-mt-7">
              <button
                onClick={() => setView("description")}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  isWhiteLabelingEnable 
                    ? 'text-gray-500 hover:text-gray-900' 
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Job Description
              </button>
            </div>

            {/* Form Container - Theme matched with Role Overview */}
            <div >
            {enableCustomATSQuestions ? (
              <CustomATSForm
                linkToken={linkToken}
                jobDetails={jobDetails}
                branding={branding}
                isWhiteLabelingEnable={isWhiteLabelingEnable}
                host={host}
                setApplicationSubmitted={setApplicationSubmitted}
                setApplicationClosed={setApplicationClosed}
                enableCustomATSQuestions={enableCustomATSQuestions}
              />
            ) : (
              <StandardATSForm
                linkToken={linkToken}
                jobDetails={jobDetails}
                branding={branding}
                isWhiteLabelingEnable={isWhiteLabelingEnable}
                host={host}
                setApplicationSubmitted={setApplicationSubmitted}
                setApplicationClosed={setApplicationClosed}
              />
            )}
            </div>
          </div>

          {/* RIGHT – Job Details sticky sidebar */}
          <div className="w-full lg:w-80 lg:sticky lg:top-8">
            <div className={`${isWhiteLabelingEnable ? 'bg-[#FAFAFA]' : 'glass-effect'} rounded-2xl p-6 shadow`}>
              <JobDetails jobDetails={jobDetails} isWhiteLabelingEnable={isWhiteLabelingEnable} />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default PublicJobApplication;
